import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbAccordionModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { PreDefinedSlotServiceCategoryGroup, PreDefinedSlotServiceDao } from '../../../../db-models/pre-defined-slot-service-dao';
import { Partner } from '../../../../models/global';
import { WidgetColorConf } from '../../../../models/widget-color.model';
import { CalioCurrencyPipe } from '../../../../shared/pipes/calio-currency.pipe';
import { HourReplacePipe } from '../../../../shared/pipes/hour-replace.pipe';
import { MinuteReplacePipe } from '../../../../shared/pipes/minute-replace.pipe';
import { ReplaceCommaPipe } from '../../../../shared/pipes/replace-comma.pipe';
import { TranslationPipe } from '../../../../shared/pipes/translation.pipe';
import { CwCheckboxRadioButtonComponent } from '../../../common/theme/cw-checkbox-radio-button/cw-checkbox-radio-button.component';
import { CwMultiLangTitleComponent } from '../../../common/theme/cw-multi-lang-title/cw-multi-lang-title.component';
import { CwQuestionMarkComponent } from '../../../common/theme/cw-question-mark/cw-question-mark.component';
import { NewAlertComponent } from '../../../../shared/components/new-alert/new-alert.component';
import { isVatAddedOnTop, vatTypeLabelKey } from '../../../../services/vat-calculation.util';

const modules = [FormsModule, TranslateModule, NgbAccordionModule, FontAwesomeModule];
const components = [CwCheckboxRadioButtonComponent, CwMultiLangTitleComponent, CwQuestionMarkComponent, NewAlertComponent];
const pipes = [CalioCurrencyPipe, HourReplacePipe, MinuteReplacePipe, ReplaceCommaPipe, TranslationPipe];

@Component({
  selector: 'app-pre-defined-slot-services',
  templateUrl: './pre-defined-slot-services.component.html',
  styleUrl: './pre-defined-slot-services.component.scss',
  imports: [NgClass, ...modules, ...components, ...pipes]
})
export class PreDefinedSlotServicesComponent implements OnInit {
  protected readonly vatTypeLabelKey = vatTypeLabelKey;
  protected readonly isVatAddedOnTop = isVatAddedOnTop;

  @Input() serviceCategories: PreDefinedSlotServiceCategoryGroup[] = [];
  @Input() partner: Partner;
  @Input() lang: string;
  @Input() selectedService: PreDefinedSlotServiceDao;
  @Input() widgetColorConf: WidgetColorConf;

  @Output() serviceSelected = new EventEmitter<PreDefinedSlotServiceDao>();
  @Output() shortcutNavigateToSlots = new EventEmitter<void>();

  protected activePanels: string[] = [];
  protected showShortcutButton = false;
  protected searchServiceQuery = '';
  protected filteredServiceCategories: PreDefinedSlotServiceCategoryGroup[] = [];

  ngOnInit(): void {
    this.filteredServiceCategories = this.serviceCategories;
    this.expandCollapseCategoriesBasedOnSetting();
    this.partner?.is_widget_service_shortcut_button_enabled === 1 && (this.showShortcutButton = true);
    this.checkAutoSkip();
  }

  protected onSelectService(service: PreDefinedSlotServiceDao): void {
    this.serviceSelected.emit(service);
  }

  protected onSearchServiceQueryChanged(): void {
    if (!this.searchServiceQuery) {
      this.filteredServiceCategories = this.serviceCategories;
    } else {
      const query = this.searchServiceQuery.toLowerCase();
      this.filteredServiceCategories = this.serviceCategories
        .map(group => {
          const filtered = group.services.filter(service => {
            const name = service.name?.toLowerCase();
            const description = service.description?.toLowerCase();
            return (name && name.indexOf(query) > -1) || (description && description.indexOf(query) > -1);
          });
          return filtered.length ? { ...group, services: filtered } : null;
        })
        .filter(Boolean) as PreDefinedSlotServiceCategoryGroup[];
    }

    this.activePanels = this.filteredServiceCategories.map((_cat, i) => `static-${i}`);
  }

  private checkAutoSkip(): void {
    if (Number(this.partner?.widget_jumps_to_resources_step_when_single_service) !== 1) {
      return;
    }

    const allServices = [].concat(...this.serviceCategories.map(g => g.services));
    if (allServices.length === 1) {
      this.serviceSelected.emit(allServices[0]);
      setTimeout(() => this.shortcutNavigateToSlots.emit(), 300);
    }
  }

  private expandCollapseCategoriesBasedOnSetting(): void {
    const status = Number(this.partner?.widget_appointment_service_category_status);
    if (status === 2) {
      this.activePanels = this.serviceCategories.map((_cat, i) => `static-${i}`);
    } else if (status === 1) {
      this.activePanels = ['static-0'];
    } else {
      this.activePanels = [];
    }
  }
}
